/**
 * Battle-orkestratie: kaart spelen, beurtwissel, baas-telegraferen & resolutie,
 * passives en win/verlies. Publieke acties klonen de state en geven 'm terug.
 */
import type { Aanval, KaartType, Status } from '../data/types';
import type { BattleState, KaartInst } from './state';
import { HANDGROOTTE, kloon, log, rngInt, rngShuffle, trek } from './state';
import { speelKaartEffect } from './effects';
import { aanvalGenegeerd, evalueerCombos, pasZwakteBijSpelen } from './weakness';
import { heeftStatus, vindStatus, voegStatusToe } from './mutators';

/* ------------------------------------------------------------------ */
/* Kosten & speelbaarheid                                              */
/* ------------------------------------------------------------------ */

export function effectieveKost(state: BattleState, inst: KaartInst): number {
  const sp = state.speler;
  const k = inst.kaart;
  if (sp.freeAll) return 0;
  if (sp.freeTypes.includes(k.type)) return 0;
  if (sp.freeNextCount > 0) return 0;
  let kost = k.cost;
  kost -= sp.costCutDeze;
  for (const c of sp.costDownType) if (c.type === k.type) kost -= c.amt;
  // costUp-status
  const cu = sp.statuses.filter((s) => s.kind === 'costUp' && s.resterend > 0);
  for (const s of cu) if (s.type === 'all' || s.type === k.type) kost += s.amt ?? 0;
  return Math.max(0, kost);
}

/** Mag deze kaart nu gespeeld worden? Geeft reden terug (of null = mag wel). */
export function speelbaarReden(state: BattleState, inst: KaartInst): string | null {
  const sp = state.speler;
  const k = inst.kaart;
  if (state.resultaat !== 'bezig') return 'Gevecht voorbij';
  if (heeftStatus(state, 'skipTurn')) return 'Beurt overgeslagen';
  if (state.bevroren.includes(inst.instId)) return 'Bevroren';
  const lockT = sp.statuses.find((s) => s.kind === 'lockType' && s.resterend > 0 && s.type === k.type);
  if (lockT) return `${k.type}-kaarten geblokkeerd`;
  const lockO = sp.statuses.find((s) => s.kind === 'lockOrigin' && s.resterend > 0 && s.origin === k.origin);
  if (lockO) return 'Origin geblokkeerd';
  const only = sp.statuses.find((s) => s.kind === 'onlyType' && s.resterend > 0);
  if (only && only.type !== k.type) return `Alleen ${only.type}-kaarten`;
  const maxP = vindStatus(state, 'maxPlay');
  if (maxP && sp.gespeeldDezeBeurt.length >= (maxP.amt ?? 99)) return 'Maximaal aantal kaarten gespeeld';
  if (effectieveKost(state, inst) > sp.energie) return 'Te weinig energie';
  return null;
}

export function kanSpelen(state: BattleState, inst: KaartInst): boolean {
  return speelbaarReden(state, inst) === null;
}

/* ------------------------------------------------------------------ */
/* Kaart spelen                                                        */
/* ------------------------------------------------------------------ */

export function speelKaart(state: BattleState, instId: string): BattleState {
  const ns = kloon(state);
  ns.tonen = [];
  const idx = ns.hand.findIndex((i) => i.instId === instId);
  if (idx === -1) return state;
  const inst = ns.hand[idx];
  if (!kanSpelen(ns, inst)) return state;

  const kost = effectieveKost(ns, inst);
  ns.speler.energie -= kost;
  if (ns.speler.freeNextCount > 0) ns.speler.freeNextCount -= 1;

  ns.hand.splice(idx, 1);
  ns.discard.push(inst);

  // effect + tracking
  speelKaartEffect(ns, inst.kaart);
  ns.speler.gespeeldDezeBeurt.push(inst.kaart);
  ns.speler.typeTellingTotaal[inst.kaart.type] += 1;

  // zwakte-bonussen + combo's
  pasZwakteBijSpelen(ns, inst.kaart.naam, inst.kaart.type);
  evalueerCombos(ns);

  controleerResultaat(ns);
  return ns;
}

/* ------------------------------------------------------------------ */
/* Beurtwissel                                                         */
/* ------------------------------------------------------------------ */

function tikDuren(state: BattleState): void {
  const sp = state.speler;
  // statuses
  for (const s of sp.statuses) s.resterend -= 1;
  sp.statuses = sp.statuses.filter((s) => s.resterend > 0);
  // multi-turn buffs/costdown
  for (const b of sp.buffOutput) b.resterend -= 1;
  sp.buffOutput = sp.buffOutput.filter((b) => b.resterend > 0);
  for (const c of sp.costDownType) c.resterend -= 1;
  sp.costDownType = sp.costDownType.filter((c) => c.resterend > 0);
  // weaken/drain op de baas
  if (state.baasState.weakenTurns > 0) state.baasState.weakenTurns -= 1;
  if (state.baasState.weakenTurns === 0) state.baasState.weakenAmt = 0;
  if (state.baasState.drainTurns > 0) state.baasState.drainTurns -= 1;
  if (state.baasState.drainTurns === 0) state.baasState.drainAmt = 0;
  if (sp.veilImmuneTurns > 0) sp.veilImmuneTurns -= 1;
}

/** Reset beurt-gebonden modifiers (gelden maar 1 beurt). */
function resetBeurtModifiers(state: BattleState): void {
  const sp = state.speler;
  sp.buffNext = 0;
  sp.costCutDeze = 0;
  sp.freeAll = false;
  sp.freeTypes = [];
  sp.multDeze = [];
  sp.gespeeldDezeBeurt = [];
  sp.blockDezeBeurt = 0;
  sp.toegepasteCombos = [];
}

/** Start-of-turn passives + telegraferen. Gebruikt voor elke speler-beurt. */
function voorbereidBeurt(state: BattleState, eerste: boolean): void {
  const sp = state.speler;
  const bs = state.baasState;

  // aura herhaalt elke beurt
  if (sp.aura) {
    if (sp.aura.energy) sp.energie += sp.aura.energy;
    if (sp.aura.block) {
      sp.schild += sp.aura.block;
      sp.schildTurns = Math.max(sp.schildTurns, 1);
      sp.blockDezeBeurt += sp.aura.block;
      if (bs.baas.verbindingFight) bs.verbinding = Math.min(100, bs.verbinding + sp.aura.block);
      else if (!heeftStatus(state, 'noBossDmg')) bs.hp = Math.max(0, bs.hp - sp.aura.block);
    }
    if (sp.aura.cleanseVeil) sp.statuses = sp.statuses.filter((s) => s.kind !== 'veil');
  }

  // healOverTime
  for (const h of sp.healOverTime) {
    if (!heeftStatus(state, 'nullifyHeal')) sp.hp = Math.min(sp.maxHp, sp.hp + h.amt);
    h.resterend -= 1;
  }
  sp.healOverTime = sp.healOverTime.filter((h) => h.resterend > 0);

  // energyDrainPerTurn
  const drain = vindStatus(state, 'energyDrainPerTurn');
  if (drain) sp.energie = Math.max(0, sp.energie - (drain.amt ?? 0));

  // baas-specifieke start-passives
  startPassieven(state, eerste);

  // baas-energie voor deze ronde (na drain) + intent kiezen (telegraferen)
  bs.energie = Math.max(0, bs.baas.energie - (bs.drainTurns > 0 ? bs.drainAmt : 0));
  bs.intent = kiesIntent(state);

  // duurste starthand-kost (voor conditionalDmg)
  sp.duursteStarthandCost = state.hand.reduce((m, i) => Math.max(m, i.kaart.cost), 0);
}

/**
 * Bereidt de éérste speler-beurt voor (na initBattle): zet beurt=1, telegrafeer
 * de eerste aanval. De eerste hand is al door initBattle getrokken.
 */
export function startGevecht(state: BattleState): BattleState {
  const ns = kloon(state);
  ns.beurt = 1;
  voorbereidBeurt(ns, true);
  return ns;
}

function kiesIntent(state: BattleState): Aanval | null {
  const bs = state.baasState;
  const opties = bs.baas.aanvallen.filter((a) => !bs.verwijderd.includes(a.naam) && a.cost <= bs.energie);
  if (opties.length === 0) return null;
  return opties[rngInt(state.rng, opties.length)];
}

/** Begint een nieuwe speler-beurt (na een baas-resolutie). */
export function nieuweBeurt(state: BattleState): void {
  const sp = state.speler;
  state.beurt += 1;
  tikDuren(state);
  resetBeurtModifiers(state);

  // energie herstellen + opgespaarde energie (kan negatief zijn, bv. Gravuun)
  sp.energie = Math.max(0, sp.maxEnergie + sp.energieVolgende);
  sp.energieVolgende = 0;

  // bevroren reset (freeze duurt 1 beurt), daarna kunnen passives opnieuw bevriezen
  state.bevroren = [];

  // hand opnieuw vullen tot handgrootte (− drawLess)
  const dl = vindStatus(state, 'drawLess');
  const doel = Math.max(0, HANDGROOTTE - (dl?.amt ?? 0));
  trek(state, doel);

  voorbereidBeurt(state, false);
}

/* ------------------------------------------------------------------ */
/* Einde beurt → baas-resolutie                                        */
/* ------------------------------------------------------------------ */

export function eindigBeurt(state: BattleState): BattleState {
  const ns = kloon(state);
  ns.tonen = [];
  if (ns.resultaat !== 'bezig') return state;

  // 'no X gespeeld'-passives evalueren vóór het afleggen
  eindPassieven(ns);

  // handHasAllOrigins-zwakte (bonus vóór afleggen)
  evalueerHandAllOrigins(ns);

  // resterende hand afleggen (§1.2)
  ns.discard.push(...ns.hand);
  ns.hand = [];

  // baas valt aan
  resolveerBaas(ns);
  controleerResultaat(ns);

  // volgende speler-beurt
  if (ns.resultaat === 'bezig') nieuweBeurt(ns);
  return ns;
}

function evalueerHandAllOrigins(state: BattleState): void {
  const z = state.baasState.baas.zwaktes.find((w) => w.handHasAllOrigins);
  if (!z) return;
  const origins = new Set(state.hand.map((i) => i.kaart.origin));
  if (origins.size === 7 && z.bonusDmg) {
    state.baasState.hp = Math.max(0, state.baasState.hp - z.bonusDmg);
    log(state, `Alle origins in hand: baas −${z.bonusDmg} HP.`);
  }
}

function resolveerBaas(state: BattleState): void {
  const bs = state.baasState;
  const sp = state.speler;

  // Theluun: automatisch −5 Verbinding per baas-beurt (§13)
  if (bs.baas.verbindingFight) bs.verbinding = Math.max(0, bs.verbinding - 5);

  if (bs.slaOver) {
    bs.slaOver = false;
    log(state, `${bs.baas.naam} slaat zijn aanval over.`);
    vervalSchild(state);
    return;
  }

  const intent = bs.intent;
  if (!intent) {
    vervalSchild(state);
    return;
  }

  // counter?
  if (aanvalGenegeerd(state, intent)) {
    log(state, `${intent.naam} genegeerd.`);
    vervalSchild(state);
    return;
  }

  // reflect?
  if (sp.reflectVolgende) {
    sp.reflectVolgende = false;
    const baat = (intent.effect.dmg ?? 4) + sp.reflectBonus;
    sp.reflectBonus = 0;
    sp.hp = Math.min(sp.maxHp, sp.hp + baat);
    log(state, `${intent.naam} weerkaatst: +${baat} HP.`);
    vervalSchild(state);
    return;
  }

  pasAanvalToe(state, intent);
  vervalSchild(state);
}

function pasAanvalToe(state: BattleState, intent: Aanval): void {
  const bs = state.baasState;
  const sp = state.speler;
  const e = intent.effect;
  log(state, `${bs.baas.naam}: ${intent.naam}.`);

  // schade
  if (e.dmg) {
    let d = Math.max(0, e.dmg - (bs.weakenTurns > 0 ? bs.weakenAmt : 0));
    if (sp.volledigBlok) {
      sp.volledigBlok = false;
      log(state, 'Aanval volledig geblokkeerd.');
    } else {
      const absorb = Math.min(sp.schild, d);
      sp.schild -= absorb;
      d -= absorb;
      sp.hp -= d;
    }
  }
  // conditionalDmg
  if (e.conditionalDmg) {
    const duurGespeeld = sp.gespeeldDezeBeurt.some((k) => k.cost >= sp.duursteStarthandCost && sp.duursteStarthandCost > 0);
    if (!duurGespeeld) {
      let d = e.conditionalDmg.amt;
      if (sp.volledigBlok) { sp.volledigBlok = false; }
      else { const a = Math.min(sp.schild, d); sp.schild -= a; d -= a; sp.hp -= d; }
      log(state, `${intent.naam}: voorwaardelijke schade.`);
    }
  }
  if (e.verbinding) bs.verbinding = Math.max(0, Math.min(100, bs.verbinding + e.verbinding));
  if (e.energyLoss) sp.energie = Math.max(0, sp.energie - e.energyLoss);

  // statussen
  if (e.status) {
    const lijst = Array.isArray(e.status) ? e.status : [e.status];
    for (const s of lijst) voegStatusToe(state, oplosDynamisch(state, s));
  }

  // discard
  if (e.discard) doeDiscard(state, e.discard.n, e.discard.mode);
  if (e.shuffleHand) {
    state.deck = rngShuffle(state.rng, [...state.deck, ...state.hand]);
    state.hand = [];
    log(state, 'Hand teruggeschud in deck.');
  }
  if (e.shuffleOriginCardsBack) {
    const blijft: KaartInst[] = [];
    const terug: KaartInst[] = [];
    for (const i of state.hand) (i.kaart.origin === e.shuffleOriginCardsBack.excludeOrigin ? blijft : terug).push(i);
    state.hand = blijft;
    state.deck = rngShuffle(state.rng, [...state.deck, ...terug]);
    log(state, 'Kaarten teruggeschud (verbinding verbroken).');
  }
  if (e.freezeOne) bevriesWillekeurig(state, 1);
  if (e.freeze) bevriesWillekeurig(state, e.freeze);
  if (e.clearAura && sp.aura) { sp.aura = null; log(state, 'Aura verbroken.'); }
  if (e.forcePlayStrongest) forcePlay(state, 'strongest');
  if (e.forcePlayRandom) forcePlay(state, 'random');
}

/** Lost een dynamisch doeltype op (mostPlayed / leastInHand). */
function oplosDynamisch(state: BattleState, s: Status): Status {
  if (!s.dynamic) return s;
  const sp = state.speler;
  let type: KaartType = 's';
  if (s.dynamic === 'mostPlayed') {
    const t = sp.typeTellingTotaal;
    type = (Object.keys(t) as KaartType[]).reduce((a, b) => (t[b] > t[a] ? b : a), 's');
  } else {
    const telling: Record<string, number> = { s: 0, o: 0, p: 0, e: 0, m: 0, k: 0 };
    for (const i of state.hand) telling[i.kaart.type] += 1;
    type = (Object.keys(telling) as KaartType[]).reduce((a, b) => (telling[b] < telling[a] ? b : a), 's');
  }
  return { ...s, type, dynamic: undefined };
}

function vervalSchild(state: BattleState): void {
  const sp = state.speler;
  if (sp.schildTurns > 1) {
    sp.schildTurns -= 1;
  } else {
    sp.schild = 0;
    sp.schildTurns = 0;
  }
}

function doeDiscard(state: BattleState, n: number, mode: 'top' | 'random' | 'strongest'): void {
  for (let i = 0; i < n && state.hand.length > 0; i++) {
    let idx = 0;
    if (mode === 'random') idx = rngInt(state.rng, state.hand.length);
    else if (mode === 'strongest') idx = state.hand.reduce((best, cur, ci, arr) => (cur.kaart.cost > arr[best].kaart.cost ? ci : best), 0);
    const [weg] = state.hand.splice(idx, 1);
    if (weg) state.discard.push(weg);
  }
  log(state, `${n} kaart(en) afgelegd.`);
}

function bevriesWillekeurig(state: BattleState, n: number): void {
  const vrij = state.hand.filter((i) => !state.bevroren.includes(i.instId));
  for (let i = 0; i < n && vrij.length > 0; i++) {
    const idx = rngInt(state.rng, vrij.length);
    state.bevroren.push(vrij[idx].instId);
    vrij.splice(idx, 1);
  }
  if (n > 0) log(state, `${n} kaart(en) bevroren.`);
}

function forcePlay(state: BattleState, mode: 'strongest' | 'random'): void {
  const speelbaar = state.hand.filter((i) => kanSpelen(state, i));
  if (speelbaar.length === 0) return;
  let inst: KaartInst;
  if (mode === 'strongest') inst = speelbaar.reduce((a, b) => (b.kaart.cost > a.kaart.cost ? b : a));
  else inst = speelbaar[rngInt(state.rng, speelbaar.length)];
  log(state, `Gedwongen gespeeld: ${inst.kaart.naam}.`);
  const idx = state.hand.findIndex((i) => i.instId === inst.instId);
  state.hand.splice(idx, 1);
  state.discard.push(inst);
  state.speler.energie = Math.max(0, state.speler.energie - effectieveKost(state, inst));
  speelKaartEffect(state, inst.kaart);
  state.speler.gespeeldDezeBeurt.push(inst.kaart);
  state.speler.typeTellingTotaal[inst.kaart.type] += 1;
}

/* ------------------------------------------------------------------ */
/* Passives                                                            */
/* ------------------------------------------------------------------ */

function startPassieven(state: BattleState, _eerste: boolean): void {
  const bs = state.baasState;
  switch (bs.baas.id) {
    case 'ch2-morveth': {
      if (rngInt(state.rng, 100) < 40) bevriesWillekeurig(state, 1);
      break;
    }
    case 'ch6-mavoreth': {
      const immuun = state.speler.veilImmuneTurns > 0 || state.speler.veilPermanentCleared || (state.speler.aura?.cleanseVeil ?? false);
      if (!immuun) voegStatusToe(state, { kind: 'veil', turns: 1 });
      break;
    }
    default:
      break;
  }
}

function eindPassieven(state: BattleState): void {
  const bs = state.baasState;
  const sp = state.speler;
  const speeldeType = (t: KaartType) => sp.gespeeldDezeBeurt.some((k) => k.type === t);
  switch (bs.baas.id) {
    case 'ch1-kharos': {
      if (sp.blockDezeBeurt === 0) {
        bs.stacks += 1;
        log(state, `Blokkade-stack ${bs.stacks}/3.`);
        if (bs.stacks >= 3) {
          sp.hp -= 10;
          bs.stacks = 0;
          log(state, 'Oerverlamming: −10 HP.');
        }
      }
      break;
    }
    case 'ch3-gravuun':
      if (!speeldeType('s')) { sp.energieVolgende -= 1; log(state, 'Geen steen gespeeld: −1 energie volgende beurt.'); }
      break;
    case 'ch4-varuun':
      if (sp.blockDezeBeurt === 0) { bs.hp += 2; log(state, 'Gesloten hart: baas +2 HP.'); }
      break;
    case 'ch5-silvareth':
      if (!speeldeType('p')) { bs.hp += 3; log(state, 'Geen praktijk gespeeld: baas +3 HP.'); }
      break;
    default:
      break;
  }
}

/* ------------------------------------------------------------------ */
/* Win / verlies                                                       */
/* ------------------------------------------------------------------ */

export function controleerResultaat(state: BattleState): void {
  if (state.resultaat !== 'bezig') return;
  const bs = state.baasState;
  if (state.speler.hp <= 0) {
    state.speler.hp = 0;
    state.resultaat = 'verloren';
    return;
  }
  if (bs.baas.verbindingFight) {
    if (bs.verbinding >= 100) state.resultaat = 'gewonnen';
    else if (bs.verbinding <= 0) state.resultaat = 'verloren';
  } else if (bs.hp <= 0) {
    state.resultaat = 'gewonnen';
  }
}
